/* assets/cart-drawer.js */

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.activeElement = null;

    this._boundClose = this.close.bind(this);
    this._boundOnKeyUp = this.onKeyUp.bind(this);

    // ESC closes
    this.addEventListener('keyup', this._boundOnKeyUp);

    // Initial bind (may be replaced later)
    this.bindOverlayClick();
    this.bindCloseButtons();

    this.setHeaderCartIconAccessibility();
    
    // CRITICAL: Ascultă evenimente de cart update
    this.setupCartUpdateListener();
  }

  connectedCallback() {
    console.log('[CART-DRAWER] Connected to DOM');
    // Actualizează bara la încărcare (pentru când drawer-ul există deja în pagină)
    requestAnimationFrame(() => {
      this.updateProgressBarOnOpen();
      this.bindQuantityButtons();
    });
  }

  /**
   * Ascultă evenimente de actualizare coș (de la add to cart, quantity changes)
   */
  setupCartUpdateListener() {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        // Actualizează dataset-ul cu noul total
        if (event.cartData && typeof event.cartData.total_price !== 'undefined') {
          this.dataset.cartTotalCents = String(event.cartData.total_price);
          
          // Dacă drawer-ul e deschis, actualizează bara instant
          if (this.classList.contains('is-open')) {
            requestAnimationFrame(() => {
              this.updateProgressBarOnOpen();
            });
          }
        }
      });
    }
  }

  onKeyUp(evt) {
    if (evt.code === 'Escape') this.close();
  }

  bindOverlayClick() {
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (overlay) {
      overlay.removeEventListener('click', this._boundClose);
      overlay.addEventListener('click', this._boundClose);
    }
  }

  bindCloseButtons() {
    this.querySelectorAll('.drawer__close').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.close();
      });
    });
  }

  /**
   * CRITICAL: Folosim EVENT DELEGATION pentru butoanele +/-
   * Aceasta previne conflicte cu alte event listeners
   */
  bindQuantityButtons() {
    console.log('[CART-DRAWER] bindQuantityButtons called (using delegation)');
    
    // Șterge orice event listener vechi de pe drawer
    const oldListener = this._quantityClickHandler;
    if (oldListener) {
      this.removeEventListener('click', oldListener);
    }
    
    // Creează handler nou cu delegation
    this._quantityClickHandler = (e) => {
      // Găsește butonul cel mai apropiat
      const button = e.target.closest('button[name="minus"], button[name="plus"]');
      
      // Dacă nu e un buton +/-, ignore
      if (!button) return;
      
      // Verifică că butonul e într-un quantity-input
      const quantityInput = button.closest('quantity-input');
      if (!quantityInput) return;
      
      // Verifică că are SVG (icon-plus sau icon-minus)
      const hasSvg = button.querySelector('.svg-wrapper, svg');
      if (!hasSvg) {
        console.warn('[CART-DRAWER] Button has no SVG icon, ignoring:', button);
        return;
      }
      
      console.log('[CART-DRAWER] Quantity button clicked:', button.getAttribute('name'));
      
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation(); // Stop ALL handlers
      
      const input = quantityInput.querySelector('.quantity__input');
      if (!input) return;
      
      const currentValue = parseInt(input.value) || 0;
      const min = parseInt(input.getAttribute('min')) || 0;
      const max = input.getAttribute('max');
      const step = parseInt(input.getAttribute('step')) || 1;
      
      let newValue = currentValue;
      
      if (button.getAttribute('name') === 'minus') {
        newValue = Math.max(min, currentValue - step);
        console.log(`[CART-DRAWER] Minus: ${currentValue} -> ${newValue}`);
      } else if (button.getAttribute('name') === 'plus') {
        newValue = currentValue + step;
        if (max) newValue = Math.min(parseInt(max), newValue);
        console.log(`[CART-DRAWER] Plus: ${currentValue} -> ${newValue}`);
      }
      
      if (newValue !== currentValue) {
        input.value = newValue;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    };
    
    // Attach la drawer cu capture=true pentru a prinde evenimentul ÎNAINTE de alții
    this.addEventListener('click', this._quantityClickHandler, true);
    
    console.log('[CART-DRAWER] Event delegation attached');
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');

    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });

    cartLink.addEventListener('keydown', (event) => {
      if (event.code && event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);

    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) {
      this.setSummaryAccessibility(cartDrawerNote);
    }

    this.classList.add('is-open');
    document.body.classList.add('overflow-hidden');

    // Actualizează bara de progres DOAR dacă elementele există
    // Așteaptă un frame pentru ca DOM-ul să fie complet încărcat
    requestAnimationFrame(() => {
      this.updateProgressBarOnOpen();
      this.bindQuantityButtons();
    });

    // Focus trap on transition end
    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');

        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');

        if (typeof trapFocus === 'function' && containerToTrapFocusOn && focusElement) {
          trapFocus(containerToTrapFocusOn, focusElement);
        } else if (focusElement && typeof focusElement.focus === 'function') {
          focusElement.focus();
        }
      },
      { once: true }
    );
  }

  /**
   * Actualizează bara de progres INSTANT la deschiderea drawer-ului
   */
  updateProgressBarOnOpen() {
    console.log('[CART-DRAWER] updateProgressBarOnOpen called');
    
    const el = this.querySelector('#free-shipping-threshold');
    const progressEl = this.querySelector('#progress');
    const messageEl = this.querySelector('#free-shipping-message');
    
    console.log('[CART-DRAWER] Elements found:', {
      threshold: !!el,
      progress: !!progressEl,
      message: !!messageEl
    });
    
    // Verificare robustă - dacă elementele nu există, ieșim
    if (!el || !progressEl || !messageEl) {
      console.warn('[CART-DRAWER] Free shipping elements not found in drawer');
      return;
    }

    const thresholdRON = parseFloat(el.dataset.thresholdRon || '150');
    const thresholdMDL = parseFloat(el.dataset.thresholdMdl || '600');
    const currencySymbol = el.dataset.currencySymbol || 'lei';
    
    console.log('[CART-DRAWER] Thresholds:', { thresholdRON, thresholdMDL, currencySymbol });
    
    // Detectează corect MDL/lei
    const isMDL = (currencySymbol === 'L' || currencySymbol === 'lei' || currencySymbol === 'MDL');
    const threshold = isMDL ? thresholdMDL : thresholdRON;
    const currencyToShow = isMDL ? 'lei' : 'RON';

    // Preia totalul din dataset
    const cartTotalCents = parseInt(this.dataset.cartTotalCents || '0', 10);
    const cartTotal = cartTotalCents / 100;
    
    console.log('[CART-DRAWER] Cart total:', cartTotal, 'Threshold:', threshold);

    // Calculează procentul
    const pct = threshold > 0 ? Math.min((cartTotal / threshold) * 100, 100) : 0;
    
    console.log('[CART-DRAWER] Progress percentage:', pct);
    
    // ACTUALIZARE VIZUALĂ INSTANT - SAFE
    try {
      el.textContent = `${threshold} ${currencyToShow}`;
      progressEl.style.width = `${pct}%`;
      
      if (threshold > 0 && cartTotal >= threshold) {
        messageEl.innerHTML = 'Congratulations, you have <strong>free shipping</strong>!';
        messageEl.classList.add('success');
        progressEl.style.setProperty('background', 'linear-gradient(90deg, #059669 0%, #10b981 100%)', 'important');
        console.log('[CART-DRAWER] Free shipping achieved!');
      } else {
        messageEl.classList.remove('success');
        progressEl.style.setProperty('background', 'linear-gradient(90deg, #8C6A4F 0%, #a67c5d 100%)', 'important');
        
        const remaining = Math.max(threshold - cartTotal, 0);
        messageEl.innerHTML = `You're <strong>${remaining.toFixed(2)} ${currencyToShow}</strong> away from FREE standard shipping`;
        console.log('[CART-DRAWER] Remaining for free shipping:', remaining);
      }
    } catch (e) {
      console.error('[CART-DRAWER] Error updating progress bar:', e);
    }
  }

  close() {
    this.classList.remove('is-open');

    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(this.activeElement);
    }

    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling?.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      const details = event.currentTarget.closest('details');
      event.currentTarget.setAttribute('aria-expanded', !(details && details.hasAttribute('open')));
    });

    if (typeof onKeyUpEscape === 'function') {
      cartDrawerNote.parentElement?.addEventListener('keyup', onKeyUpEscape);
    }
  }

  // În cart-drawer.js, înlocuiește metoda renderContents cu:

renderContents(parsedState) {
  console.log('[CART-DRAWER] renderContents called with:', parsedState);
  
  const inner = this.querySelector('.drawer__inner');
  
  // CRITICAL: Șterge clasa is-empty de pe drawer
  if (this.classList.contains('is-empty')) {
    console.log('[CART-DRAWER] Removing is-empty class');
    this.classList.remove('is-empty');
  }
  
  if (inner && inner.classList.contains('is-empty')) {
    inner.classList.remove('is-empty');
  }

  this.productId = parsedState?.id;

  // Replace sections
  this.getSectionsToRender().forEach((section) => {
    console.log('[CART-DRAWER] Rendering section:', section.id);
    
    const sectionElement = section.selector
      ? this.querySelector(section.selector) || document.querySelector(section.selector)
      : document.getElementById(section.id);

    if (!sectionElement) {
      console.warn('[CART-DRAWER] Section element not found:', section);
      return;
    }

    const sectionHtml = parsedState?.sections?.[section.id];
    if (!sectionHtml) {
      console.warn('[CART-DRAWER] No HTML for section:', section.id);
      return;
    }

    console.log('[CART-DRAWER] Updating section HTML:', section.id);
    sectionElement.innerHTML = this.getSectionInnerHTML(sectionHtml, section.selector);
  });

  // DOM replaced: rebind overlay și butoane
  this.bindOverlayClick();
  
  // CRITICAL: Fetch totalul actualizat de la /cart.js și actualizează bara
  fetch('/cart.js')
    .then((r) => r.json())
    .then((cart) => {
      console.log('[CART-DRAWER] Cart refreshed after render:', cart);
      
      // Actualizează dataset-ul cu totalul REAL
      this.dataset.cartTotalCents = String(cart.total_price);
      
      // Re-bind butoanele +/- și actualizează bara
      requestAnimationFrame(() => {
        this.bindQuantityButtons();
        this.updateProgressBarOnOpen();
      });
    })
    .catch((e) => {
      console.error('[CART-DRAWER] Error fetching cart total:', e);
      // Fallback: încearcă să folosești parsedState (dacă are total_price)
      if (parsedState && typeof parsedState.total_price !== 'undefined') {
        this.dataset.cartTotalCents = String(parsedState.total_price);
      }
      
      requestAnimationFrame(() => {
        this.bindQuantityButtons();
        this.updateProgressBarOnOpen();
      });
    });

  // Keep it open
  console.log('[CART-DRAWER] Opening drawer');
  this.open();
}

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const el = doc.querySelector(selector);
    return el ? el.innerHTML : '';
  }

  getSectionsToRender() {
    return [
      { id: 'cart-drawer', selector: '#CartDrawer' },
      { id: 'cart-icon-bubble' },
    ];
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}

/**
 * CartDrawerItems extends CartItems from cart.js
 */
class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble', 
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

if (!customElements.get('cart-drawer-items')) {
  customElements.define('cart-drawer-items', CartDrawerItems);
}

